import { API_BASE_URL } from '../config/api';
import type { SavedSearch, Business } from '../types';

export interface SaveSearchPayload {
  name: string;
  queryText: string;
  results: Business[];
}

export interface CombinedSearchResult {
  name: string;
  query: string;
  results: Business[];
  resultCount: number;
}

/**
 * Fetch all saved searches for the current user
 */
export async function fetchSavedSearches(): Promise<SavedSearch[]> {
  const response = await fetch(`${API_BASE_URL}/saved-searches`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch saved searches' }));
    throw new Error(error.message || 'Failed to fetch saved searches');
  }

  const data = await response.json();
  return data.savedSearches || [];
}

/**
 * Save a new search
 */
export async function saveSearch(payload: SaveSearchPayload): Promise<SavedSearch> {
  const response = await fetch(`${API_BASE_URL}/saved-searches`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to save search' }));
    throw new Error(error.message || 'Failed to save search');
  }

  const data = await response.json();
  return data.savedSearch;
}

/**
 * Load a specific saved search
 */
export async function loadSavedSearch(id: number): Promise<SavedSearch> {
  const response = await fetch(`${API_BASE_URL}/saved-searches/${id}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to load saved search' }));
    throw new Error(error.message || 'Failed to load saved search');
  }

  const data = await response.json();
  return data.savedSearch;
}

/**
 * Combine multiple saved searches
 */
export async function combineSavedSearches(ids: number[]): Promise<CombinedSearchResult> {
  const response = await fetch(`${API_BASE_URL}/saved-searches/combine`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ searchIds: ids }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to combine searches' }));
    throw new Error(error.message || 'Failed to combine searches');
  }

  const data = await response.json();
  return data;
}

/**
 * Update a saved search (add more results)
 */
export async function updateSavedSearch(id: number, newResults: Business[]): Promise<{ added: number; duplicatesRemoved: number; total: number }> {
  const response = await fetch(`${API_BASE_URL}/saved-searches/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ results: newResults }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to update search' }));
    throw new Error(error.message || 'Failed to update search');
  }

  const data = await response.json();
  return {
    added: data.added,
    duplicatesRemoved: data.duplicatesRemoved,
    total: data.savedSearch.results_count,
  };
}

/**
 * Delete a saved search
 */
export async function deleteSavedSearch(id: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/saved-searches/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to delete search' }));
    throw new Error(error.message || 'Failed to delete search');
  }
}
